import { useCallback } from 'react';
import { nodesApi } from '@/api/nodes';
import { useResource } from '@/hooks/useResource';
import type { PleskDomainListResponse } from '@/types/node';

/**
 * Cached Plesk domains of a node (pass null to disable)
 */
export function usePleskDomains(nodeId: string | null) {
  const fetcher = useCallback(
    (signal: AbortSignal) => nodesApi.getPleskDomains(nodeId as string, 1, 50, { signal }),
    [nodeId]
  );

  const resource = useResource<PleskDomainListResponse>(nodeId ? fetcher : null, {
    errorMessage: 'Failed to load domains',
    keepPreviousData: false,
  });
  const { setData } = resource;

  /**
   * Ask the node for a fresh domain list, then reload the cache
   */
  const refresh = useCallback(async () => {
    if (!nodeId) return;
    await nodesApi.refreshPleskDomains(nodeId);
    const response = await nodesApi.getPleskDomains(nodeId);
    setData(response);
  }, [nodeId, setData]);

  return {
    domains: resource.data?.domains ?? [],
    total: resource.data?.total ?? 0,
    lastCachedAt: resource.data?.last_cached_at ?? null,
    isLoading: resource.isLoading,
    error: resource.error,
    refresh,
  };
}
